import { RefObject, useLayoutEffect, useRef, useState } from 'react';
import { IMonitorSalesProductItem } from 'entities/MonitorSales/MonitorSales.models';
import { buildCategoryRanks, pickCategoryShade } from 'entities/MonitorSales/monitorSales.categoryColors';

const TREEMAP_COLOR = '#3359ff';
const MIN_TILE_AREA = 6500;
const MIN_CANVAS_WIDTH = 900;
const CATEGORY_HEADER_HEIGHT = 28;

type IRgb = [number, number, number];

function hexToRgb(hex: string): IRgb {
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

const TREEMAP_COLOR_RGB = hexToRgb(TREEMAP_COLOR);

// ─── Squarified treemap layout (Bruls, Huizing, van Wijk 1999) ────────────────

interface ITreemapRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ITreemapNode<T> extends ITreemapRect {
  item: T;
}

function squarify<T>(items: T[], value: (item: T) => number, rect: ITreemapRect): ITreemapNode<T>[] {
  const total = items.reduce((sum, item) => sum + value(item), 0);
  if (!items.length || total <= 0 || rect.width <= 0 || rect.height <= 0) {
    return [];
  }

  const area = rect.width * rect.height;
  const scaled = items.map((item) => ({ item, area: (value(item) / total) * area }));

  const worstRatio = (row: typeof scaled, rowSum: number, side: number) => {
    const thickness = rowSum / side;
    return row.reduce((worst, r) => {
      const length = r.area / thickness;
      return Math.max(worst, thickness / length, length / thickness);
    }, 0);
  };

  const results: ITreemapNode<T>[] = [];
  let remaining = scaled;
  let { x, y, width, height } = rect;

  while (remaining.length) {
    const side = Math.min(width, height);
    const row: typeof remaining = [];
    let rowSum = 0;
    let bestWorst = Infinity;
    let i = 0;

    while (i < remaining.length) {
      const nextSum = rowSum + remaining[i].area;
      const worst = worstRatio([...row, remaining[i]], nextSum, side);
      if (row.length === 0 || worst <= bestWorst) {
        row.push(remaining[i]);
        rowSum = nextSum;
        bestWorst = worst;
        i += 1;
      } else {
        break;
      }
    }

    remaining = remaining.slice(row.length);
    const thickness = rowSum / side;

    if (width >= height) {
      let cy = y;
      row.forEach((r) => {
        const h = r.area / thickness;
        results.push({ x, y: cy, width: thickness, height: h, item: r.item });
        cy += h;
      });
      x += thickness;
      width -= thickness;
    } else {
      let cx = x;
      row.forEach((r) => {
        const w = r.area / thickness;
        results.push({ x: cx, y, width: w, height: thickness, item: r.item });
        cx += w;
      });
      y += thickness;
      height -= thickness;
    }
  }

  return results;
}

// Lighter shades for lower-ranked items; text color adapts for contrast against the tile background
function getShade(baseRgb: IRgb, index: number, count: number): { background: string; color: string } {
  const ratio = count > 1 ? (index / (count - 1)) * 0.7 : 0;
  const mix = (c: number) => Math.round(c + (255 - c) * ratio);
  const [r, g, b] = baseRgb.map(mix);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return { background: `rgb(${r}, ${g}, ${b})`, color: luminance > 0.6 ? '#1a1a2e' : '#ffffff' };
}

interface ICategoryGroup {
  category: string;
  items: IMonitorSalesProductItem[];
  total: number;
}

// Groups products by category, sorted by total salesPercent desc; items keep their incoming (desc) order
function groupByCategory(products: IMonitorSalesProductItem[]): ICategoryGroup[] {
  const order: string[] = [];
  const map = new Map<string, IMonitorSalesProductItem[]>();

  products.forEach((product) => {
    if (!map.has(product.category)) {
      map.set(product.category, []);
      order.push(product.category);
    }
    map.get(product.category)?.push(product);
  });

  return order
    .map((category) => {
      const items = map.get(category) ?? [];
      return { category, items, total: items.reduce((sum, item) => sum + item.salesPercent, 0) };
    })
    .sort((a, b) => b.total - a.total);
}

export interface IProductSalesTreeMapCell {
  key: string;
  x: number;
  y: number;
  width: number;
  height: number;
  item: IMonitorSalesProductItem;
  background: string;
  color: string;
}

export interface IProductSalesTreeMapGroup {
  key: string;
  category: string;
  total: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface IUseProductSalesTreeMapResult {
  containerRef: RefObject<HTMLDivElement>;
  canvasWidth: number;
  canvasHeight: number;
  cells: IProductSalesTreeMapCell[];
  groups: IProductSalesTreeMapGroup[];
}

// Measures the scrollable viewport, lays products out as a squarified treemap sized by salesPercent,
// and shades tiles from the base color by rank so larger shares read darker. When grouped, categories
// are laid out first (sized by their total share), then each category's products fill its own sub-rect.
export function useProductSalesTreeMap(
  products: IMonitorSalesProductItem[],
  open: boolean,
  showCategoryColors: boolean,
  groupSegments: boolean
): IUseProductSalesTreeMapResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    const measure = () => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      if (width > 0 && height > 0) {
        setViewportSize({ width, height });
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [open]);

  const hasSize = viewportSize.width > 0 && viewportSize.height > 0;

  // Grow both dimensions together (keeping the viewport's aspect ratio) once the desired tile area
  // no longer fits, so a large product list scrolls both horizontally and vertically, not just down.
  const viewportArea = viewportSize.width * viewportSize.height;
  const desiredArea = products.length * MIN_TILE_AREA;
  const scale = viewportArea > 0 ? Math.sqrt(Math.max(desiredArea / viewportArea, 1)) : 1;
  const canvasWidth = Math.max(viewportSize.width * scale, MIN_CANVAS_WIDTH);
  const canvasHeight = Math.max(viewportSize.height * scale, viewportSize.height);

  let cells: IProductSalesTreeMapCell[] = [];
  let groups: IProductSalesTreeMapGroup[] = [];

  if (hasSize && showCategoryColors && groupSegments) {
    const categoryGroups = groupByCategory(products);
    const outerNodes = squarify(categoryGroups, (group) => group.total, {
      x: 0,
      y: 0,
      width: canvasWidth,
      height: canvasHeight,
    });

    groups = outerNodes.map((node) => ({
      key: node.item.category,
      category: node.item.category,
      total: node.item.total,
      x: node.x,
      y: node.y,
      width: node.width,
      height: node.height,
    }));

    cells = outerNodes.flatMap((node) => {
      const innerHeight = Math.max(node.height - CATEGORY_HEADER_HEIGHT, 0);
      const innerNodes = squarify(node.item.items, (product) => product.salesPercent, {
        x: 0,
        y: 0,
        width: node.width,
        height: innerHeight,
      });
      const count = node.item.items.length;

      return innerNodes.map((inner, index) => {
        const rank = node.item.items.indexOf(inner.item);
        const shade = pickCategoryShade(node.item.category, rank, count);

        return {
          key: `${inner.item.code}-${index}`,
          x: node.x + inner.x,
          y: node.y + CATEGORY_HEADER_HEIGHT + inner.y,
          width: inner.width,
          height: inner.height,
          item: inner.item,
          background: shade.background,
          color: shade.color,
        };
      });
    });
  } else if (hasSize) {
    const nodes = squarify(products, (product) => product.salesPercent, {
      x: 0,
      y: 0,
      width: canvasWidth,
      height: canvasHeight,
    });
    const categoryRanks = showCategoryColors ? buildCategoryRanks(products) : null;

    cells = nodes.map((node, index) => {
      let shade: { background: string; color: string };

      if (categoryRanks) {
        const rank = categoryRanks.rankInCategory.get(node.item) ?? 0;
        const count = categoryRanks.countByCategory.get(node.item.category) ?? 1;
        shade = pickCategoryShade(node.item.category, rank, count);
      } else {
        const rank = products.indexOf(node.item);
        shade = getShade(TREEMAP_COLOR_RGB, rank, products.length);
      }

      return {
        key: `${node.item.code}-${index}`,
        x: node.x,
        y: node.y,
        width: node.width,
        height: node.height,
        item: node.item,
        background: shade.background,
        color: shade.color,
      };
    });
  }

  return { containerRef, canvasWidth, canvasHeight, cells, groups };
}
